import { FormEvent, useEffect, useState } from 'react'
import { CheckSquare, Save, ShieldCheck, X } from 'lucide-react'

export type StructuredActionSubmission = {
  category: string
  reason: string
  evidence?: File
}

type Props = {
  actionKey: string
  title: string
  eyebrow: string
  open: boolean
  description: string
  impact?: string[]
  categories: Array<{ value: string, label: string }>
  checklist: string[]
  evidenceHint?: string
  reasonMaxLength?: number
  submittedTitle?: string
  submitLabel?: string
  confirmationHint?: string
  saving?: boolean
  error?: string
  onClose: () => void
  onSubmit: (value: StructuredActionSubmission) => Promise<{ reference?: string, message: string }>
}

type Draft = { category: string, reason: string, confirmed: boolean, evidenceName?: string }

const storageKey = (key: string) => `license-center.action-draft.${key}`

/** Keeps destructive actions reviewable and recoverable instead of relying on browser dialogs. */
export function StructuredActionDialog({ actionKey, title, eyebrow, open, description, impact = [], categories,
  checklist, evidenceHint, reasonMaxLength = 500, submittedTitle = '已提交，等待流程处理',
  submitLabel = '提交并跟踪', confirmationHint = '提交后会写入审计记录，并按既定审批流程处理。',
  saving = false, error, onClose, onSubmit }: Props) {
  const [category, setCategory] = useState(categories[0]?.value || '')
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [evidence, setEvidence] = useState<File>()
  const [draftNote, setDraftNote] = useState('')
  const [submitted, setSubmitted] = useState<{ reference?: string, message: string }>()
  const [submissionError, setSubmissionError] = useState('')

  useEffect(() => {
    if (!open) return
    const raw = localStorage.getItem(storageKey(actionKey))
    try {
      const draft = raw ? JSON.parse(raw) as Draft : undefined
      setCategory(draft?.category || categories[0]?.value || '')
      setReason(draft?.reason || '')
      setConfirmed(Boolean(draft?.confirmed))
      setDraftNote(draft?.evidenceName ? `草稿曾选择附件：${draft.evidenceName}；出于安全原因请重新选择文件。` : '')
    } catch {
      setCategory(categories[0]?.value || ''); setReason(''); setConfirmed(false); setDraftNote('')
    }
    setEvidence(undefined); setSubmitted(undefined); setSubmissionError('')
    // Categories are static for a given action key; depending on their inline array would reset typed input on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, actionKey])

  function saveDraft() {
    localStorage.setItem(storageKey(actionKey), JSON.stringify({ category, reason, confirmed, evidenceName: evidence?.name } satisfies Draft))
    setDraftNote('草稿已保存在当前浏览器。附件内容不会写入浏览器，请在提交前重新选择。')
  }
  function close() {
    setSubmitted(undefined)
    onClose()
  }
  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmissionError('')
    try {
      const result = await onSubmit({ category, reason: reason.trim(), evidence })
      localStorage.removeItem(storageKey(actionKey))
      setSubmitted(result)
    } catch (value) {
      setSubmissionError(value instanceof Error ? value.message : '提交失败')
    }
  }

  if (!open) return null
  return <div className="modal-layer"><div className="modal structured-action-modal" role="dialog" aria-modal="true" aria-labelledby={`${actionKey}-title`}>
    <div className="modal-heading"><div><small>{eyebrow}</small><h3 id={`${actionKey}-title`}>{title}</h3></div><button className="icon-button" type="button" onClick={close} aria-label="关闭"><X size={18} /></button></div>
    {submitted ? <div className="action-submitted"><ShieldCheck size={24} /><div><b>{submittedTitle}</b><p>{submitted.message}</p>{submitted.reference && <code>{submitted.reference}</code>}</div><button className="button button--primary" type="button" onClick={close}>完成</button></div> : <form onSubmit={submit}>
      <p className="structured-action-description">{description}</p>
      {impact.length > 0 && <div className="structured-action-impact"><b>影响预览</b><ul>{impact.map(item => <li key={item}>{item}</li>)}</ul></div>}
      <label>原因分类<select value={category} onChange={event => setCategory(event.target.value)} required>{categories.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label>
      <label>处理依据与说明<textarea value={reason} onChange={event => setReason(event.target.value)} maxLength={reasonMaxLength} required placeholder="说明事实、合同/安全依据、客户影响和期望处置" /></label>
      {evidenceHint && <label>证据附件（可选）<input type="file" onChange={event => setEvidence(event.target.files?.[0])} /><small>{evidence ? `待留存：${evidence.name}` : evidenceHint}</small></label>}
      <div className="structured-action-checklist"><b><CheckSquare size={15} />复核清单</b>{checklist.map(item => <span key={item}>{item}</span>)}<label className="switch-row"><input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} required /><span><b>我已完成上述复核</b><small>{confirmationHint}</small></span></label></div>
      {draftNote && <p className="structured-action-draft">{draftNote}</p>}
      {(submissionError || error) && <div className="form-error">{submissionError || error}</div>}
      <div className="modal-actions"><button type="button" className="button button--ghost" onClick={saveDraft}><Save size={15} />保存草稿</button><button type="button" className="button button--ghost" onClick={close}>取消</button><button className="button button--primary" disabled={saving || !reason.trim() || !confirmed}>{saving ? '正在提交...' : submitLabel}</button></div>
    </form>}
  </div></div>
}
